<template>
  <WorkspaceLayout class="workspace-shell">
    <router-view v-slot="{ Component, route: childRoute }">
      <Transition
        :css="false"
        @before-enter="onBeforeEnter"
        @enter="onEnter"
        @enter-cancelled="onEnterCancelled"
        @leave="onLeave"
        @leave-cancelled="onLeaveCancelled"
      >
        <keep-alive
          :include="SHELL_KEEP_ALIVE_NAMES"
          :max="SHELL_KEEP_ALIVE_MAX"
          :key="shellKeepAliveKey"
        >
          <component
            :is="Component"
            v-if="Component"
            :key="shellViewCacheKey(childRoute)"
            class="workspace-shell__page"
          />
        </keep-alive>
      </Transition>
    </router-view>

    <GlobalSettingsModal />
  </WorkspaceLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import WorkspaceLayout from './components/WorkspaceLayout.vue';
import GlobalSettingsModal from '@/features/settings/components/GlobalSettingsModal.vue';
import { useShellToolbar } from './composables/useShellToolbar';
import { useShellPageTransition, bindShellPageRouteTransition } from './composables/useShellPageTransition';
import {
  SHELL_KEEP_ALIVE_MAX,
  SHELL_KEEP_ALIVE_NAMES,
  shellViewCacheKey,
} from './constants/shellKeepAlive';

defineOptions({ name: 'WorkspaceShell' });

const store = useStore();
const router = useRouter();
const { registerActionHandler, unregisterActionHandler } = useShellToolbar();
const {
  prepareRoute,
  onBeforeEnter,
  onEnter,
  onLeave,
  onEnterCancelled,
  onLeaveCancelled,
  unblockPendingTransition,
} = useShellPageTransition();

bindShellPageRouteTransition(prepareRoute, unblockPendingTransition);

const openEmployeeCreate = () => {
  router.push({
    name: 'employee-create',
    query: { returnTo: 'employees' }
  });
};

const syncShellToolbarHandlers = () => {
  registerActionHandler('addEmployee', openEmployeeCreate);
};

/** Reset tab cache only on logout or explicit user switch. */
const shellKeepAliveKey = ref(0);

watch(
  () => store.state.users?.user?.isLogged,
  (logged, wasLogged) => {
    if (wasLogged && !logged) {
      shellKeepAliveKey.value += 1;
    }
  },
);

watch(
  () => store.state.users?.user?.id,
  (id, prevId) => {
    if (prevId != null && id != null && String(prevId) !== String(id)) {
      shellKeepAliveKey.value += 1;
    }
  },
);

onMounted(() => {
  syncShellToolbarHandlers();
});

onBeforeUnmount(() => {
  unregisterActionHandler('addEmployee');
});
</script>

<style scoped>
.workspace-shell {
  min-height: 100%;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.workspace-shell__page {
  min-height: 0;
  width: 100%;
}
</style>
